import { StripeProductInfo } from '@george-labs.com/design-system';
import type { FC, PropsWithChildren } from 'react';

import type { Account } from '@shared/products-types';
import type { FocusPageProps, FocusPageStatics } from '@shared/focus-page-ui';
import { FocusPage } from '@shared/focus-page-ui';
import { useGlobalColorVariants } from '@shared/core-ui-layout';
import { t } from '@luna/i18n';
import type { ProductWithHelpers } from '@shared/products-core';
import { useActiveProduct } from '@shared/products-core';
import { getTitleAlignedWithGeorgeApp } from '@shared/products-general-service';
import { LocalizedAmount } from '@shared/core-ui-amount';

import { ProductAvatar } from './components/ProductAvatar';

type Props = { hideProductInfo?: boolean } & Omit<FocusPageProps, 'color' | 'left'>;

export const ProductFocusPage: FC<PropsWithChildren<Props>> & FocusPageStatics = ({
    children,
    hideProductInfo,
    ...props
}) => {
    const product = useActiveProduct<ProductWithHelpers<Account>>();
    const { color } = useGlobalColorVariants();

    const amount = (
        <LocalizedAmount
            data-cy="stripe-product-info-amount"
            showCurrency
            amount={product.mainAmount}
        />
    );

    return (
        <FocusPage
            color={color}
            left={
                !hideProductInfo && (
                    <StripeProductInfo
                        amount={amount}
                        avatar={<ProductAvatar product={product} />}
                        amountLabel={t('main.menu.v2.products.detail.amountLabel_a11y')}
                        alias={
                            <span data-cy="stripe-product-info-alias">
                                {getTitleAlignedWithGeorgeApp(product)}
                            </span>
                        }
                        aliasLabel={t('main.menu.v2.products.detail.aliasLabel_a11y')}
                        type={product.productI18N}
                        typeLabel={t('main.menu.v2.products.detail.typeLabel_a11y')}
                        data-cy="stripe-product-info"
                    />
                )
            }
            {...props}
        >
            {children}
        </FocusPage>
    );
};

ProductFocusPage.SIZE = FocusPage.SIZE;
